import { getAccessToken } from "./tokenStorage";

// Same-origin: локально API проксирует dev-сервер (Vite/CRA proxy) либо
// приложение раздаётся самим Django — прямой порт бэкенда здесь не нужен.
const DEFAULT_LOCAL_API_BASE_URL = "";
const UNAUTHORIZED_EVENT = "auth:unauthorized";

function emitUnauthorizedOnce() {
  // Avoid event spam if multiple requests fail in parallel after token invalidation.
  if (window.__aufUnauthorizedEmitted) return;
  window.__aufUnauthorizedEmitted = true;
  try {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT));
  } catch {
    // ignore
  }
}

export function getApiBaseUrl() {
  // If explicitly set (recommended for production), use it.
  // Examples:
  // - REACT_APP_API_BASE_URL="https://api.example.com"
  // - REACT_APP_API_BASE_URL="http://127.0.0.1:8000"
  const fromEnvRaw = process.env.REACT_APP_API_BASE_URL;
  if (fromEnvRaw) return normalizeBaseUrl(fromEnvRaw);

  // Local debugging: same-origin — dev-сервер проксирует /api на Django
  if (process.env.REACT_APP_ENV === "local" || process.env.NODE_ENV === "development") {
    return normalizeBaseUrl(DEFAULT_LOCAL_API_BASE_URL);
  }

  // Production: same-origin (IMPORTANT: must be "" (not "/") to avoid //api/... => https://api/...)
  return "";
}

function normalizeBaseUrl(url) {
  const trimmed = String(url || "").trim();
  if (!trimmed) return "";

  // Allow absolute http(s)://
  if (/^https?:\/\//i.test(trimmed)) return trimmed.replace(/\/+$/, "");

  // Allow protocol-relative (//host)
  if (trimmed.startsWith("//")) return trimmed.replace(/\/+$/, "");

  // Allow relative path bases (e.g. "/backend") without adding scheme
  if (trimmed.startsWith("/")) return trimmed.replace(/\/+$/, "");

  // Bare host without scheme: choose current protocol to avoid mixed content in https
  const scheme = window.location.protocol === "https:" ? "https://" : "http://";
  return `${scheme}${trimmed}`.replace(/\/+$/, "");
}


async function parseJsonSafe(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function apiFetch(path, { method = "GET", headers = {}, body } = {}) {
  const token = getAccessToken();
  const isFormData = body instanceof FormData;

  const finalHeaders = {
    // Don't set Content-Type for FormData — browser sets it with boundary automatically
    ...(body && !isFormData ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...headers,
  };

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const baseUrl = getApiBaseUrl();

  const res = await fetch(`${baseUrl}${normalizedPath}`, {
    method,
    headers: finalHeaders,
    body: isFormData ? body : body ? JSON.stringify(body) : undefined,
  });

  const data = await parseJsonSafe(res);

  if (!res.ok) {
    // If server says access token is invalid/expired => force logout in UI.
    // Только если токен был отправлен: 401 без токена — это неверный логин/пароль на форме
    // входа, а не протухшая сессия. Иначе глобальный logout перезагружает страницу входа
    // и сообщение об ошибке пропадает, не успев показаться.
    if (res.status === 401 && token) {
      emitUnauthorizedOnce();
    }
    const message =
      (data && typeof data === "object" && (data.detail || data.message)) ||
      `Request failed: ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}


